import { DateTime } from "luxon";
import { NavigateFunction } from "react-router-dom";
import IViewUserSolarPlantDetailsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-solar-plant-details-response-dto";
import { EPlantStatus } from "../../../shared/oversight-core/enums/plant-status";
import { ESolarServiceProvider } from "../../../shared/oversight-core/enums/solar-service-provider";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { IChangelogEntryView } from "../../../shared/oversight-core/interfaces/change-log-entry-view";
import { IGoodWeSolarPlantView } from "../../../shared/oversight-core/interfaces/good-we-solar-plant-view";
import { IGroWattSolarPlantView } from "../../../shared/oversight-core/interfaces/gro-watt-solar-plant-view";
import { IHttpError } from "../../../shared/oversight-core/interfaces/http-errror";
import { IPowerHubSolarPlantView } from "../../../shared/oversight-core/interfaces/powerhub-solar-plant-view";
import { IScoutSolarPlantMeter } from "../../../shared/oversight-core/interfaces/scout-solar-plant-meter";
import { IScoutSolarPlantView } from "../../../shared/oversight-core/interfaces/scout-solar-plant-view";
import { IVrmSolarPlantView } from "../../../shared/oversight-core/interfaces/vrm-solar-plant-view";
import { getRelativeTimeFromTimestamp } from "../../../shared/oversight-core/utils/time-utils";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import translate from "../../../shared/oversight-core/utils/translate";

const ACTIVE_PLANT_STATUSES = new Set<string>([
  EPlantStatus.GENERATING,
  EPlantStatus.ACTIVE,
  EPlantStatus.WAITING,
]);

export type SitePlants = {
  vrm?: IVrmSolarPlantView;
  goodWe?: IGoodWeSolarPlantView;
  growatt?: IGroWattSolarPlantView;
  powerHub?: IPowerHubSolarPlantView;
  scout?: IScoutSolarPlantView;
};

export interface ISiteViewModel {
  displayName: string;
  originalName: string;
  siteAlias: string;
  address: string;
  latitude: number;
  longitude: number;
  capacity: number;
  isActive: boolean | undefined;
  identity: string | undefined;
  statusLabel: string;
  changelog: IChangelogEntryView[];
  localTime: string;
  lastUpdate: string;
  showLastUpdate: boolean;
  isStatusActive: boolean;
  logoSize: number;
  errorLogZoneId: string;
  showTabs: boolean;
}

export type PlantDetailsTrigger = (args: { plantId: string }) => {
  unwrap: () => Promise<IViewUserSolarPlantDetailsResponseDTO>;
};

export type ChangeStatusTrigger = (args: { plantId: string }) => {
  unwrap: () => Promise<unknown>;
};

export type SolarPlantView =
  IViewUserSolarPlantDetailsResponseDTO["solarPlantView"];

const firstNonEmpty = (...values: (string | null | undefined)[]): string => {
  for (const value of values) {
    if (value) {
      return value;
    }
  }
  return "";
};

// The site's name with its alias after it in brackets, e.g. "MPU-NAM-114 (Colombo office)". Just the
// one that is set when there is only one, and the name alone when the alias repeats it.
const getSiteDisplayName = (
  siteName: string | null | undefined,
  siteAlias: string | null | undefined,
): string => {
  const name = siteName?.trim() ?? "";
  const alias = siteAlias?.trim() ?? "";

  if (name && alias && alias !== name) {
    return `${name} (${alias})`;
  }
  return name || alias;
};

const toOptionalBoolean = (value?: boolean): boolean | undefined => {
  if (typeof value !== "boolean") {
    return undefined;
  }
  return value;
};

const toIdentity = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return undefined;
};

const isPlantStatusActive = (status?: string): boolean => {
  if (!status) {
    return false;
  }
  return ACTIVE_PLANT_STATUSES.has(status);
};

const formatLocalTime = (timezone: string): string => {
  return DateTime.now().setZone(timezone).toFormat("HH:mm a");
};

const formatLastUpdate = (lastTimestamp: unknown): string => {
  if (!lastTimestamp) {
    return "";
  }
  if (typeof lastTimestamp === "string") {
    return getRelativeTimeFromTimestamp(lastTimestamp);
  }
  return getRelativeTimeFromTimestamp(lastTimestamp as number);
};

const getVrmViewModel = (plant?: IVrmSolarPlantView): ISiteViewModel => {
  const timezone = plant?.tz.formattedValue || "Etc/UTC";

  return {
    displayName: getSiteDisplayName(plant?.name?.toString(), plant?.siteAlias),
    originalName: plant?.name?.toString() ?? "",
    siteAlias: plant?.siteAlias ?? "",
    address: plant?.address?.toString() ?? "",
    latitude: Number(plant?.lt.rawValue),
    longitude: Number(plant?.lg.rawValue),
    capacity: Number(plant?.capacity?.rawValue),
    isActive: toOptionalBoolean(plant?.isActive),
    identity: toIdentity(plant?.identity),
    statusLabel: plant?.ss.formattedValue ?? "",
    changelog: plant?.changelog ?? [],
    localTime: formatLocalTime(timezone),
    lastUpdate: formatLastUpdate(plant?.last_timestamp),
    showLastUpdate: true,
    isStatusActive: true,
    logoSize: 18,
    errorLogZoneId: plant?.tz.formattedValue ?? "",
    showTabs: true,
  };
};

const getGoodWeViewModel = (plant?: IGoodWeSolarPlantView): ISiteViewModel => {
  return {
    displayName: getSiteDisplayName(plant?.plantname, plant?.siteAlias),
    originalName: plant?.plantname ?? "",
    siteAlias: plant?.siteAlias ?? "",
    address: plant?.address ?? "",
    latitude: Number(plant?.latitude),
    longitude: Number(plant?.longitude),
    capacity: Number(plant?.capacity?.capacity.wattHour),
    isActive: toOptionalBoolean(plant?.isActive),
    identity: toIdentity(plant?.identity),
    statusLabel: translate(plant?.plantStatus as EPlantStatus),
    changelog: plant?.changelog ?? [],
    localTime: formatLocalTime("Etc/UTC"),
    lastUpdate: "",
    showLastUpdate: false,
    isStatusActive: isPlantStatusActive(plant?.plantStatus),
    logoSize: 18,
    errorLogZoneId: "",
    showTabs: true,
  };
};

const getGrowattViewModel = (
  plant?: IGroWattSolarPlantView,
): ISiteViewModel => {
  const timezone = plant?.timezone || "+05:30";

  return {
    displayName: getSiteDisplayName(plant?.name, plant?.siteAlias),
    originalName: plant?.name ?? "",
    siteAlias: plant?.siteAlias ?? "",
    address: plant?.address ?? "",
    latitude: Number(plant?.latitude),
    longitude: Number(plant?.longitude),
    capacity: Number(plant?.capacity?.capacity.wattHour),
    isActive: toOptionalBoolean(plant?.isActive),
    identity: toIdentity(plant?.identity),
    statusLabel: translate(plant?.plantStatus as EPlantStatus),
    changelog: plant?.changelog ?? [],
    localTime: formatLocalTime(timezone),
    lastUpdate: "",
    showLastUpdate: false,
    isStatusActive: isPlantStatusActive(plant?.plantStatus),
    logoSize: 15,
    errorLogZoneId: "",
    showTabs: true,
  };
};

const getPowerHubViewModel = (
  plant?: IPowerHubSolarPlantView,
): ISiteViewModel => {
  return {
    displayName: getSiteDisplayName(plant?.siteName, plant?.siteAlias),
    originalName: plant?.siteName ?? "",
    siteAlias: plant?.siteAlias ?? "",
    address: plant?.address ?? "",
    latitude: Number(plant?.location?.latitude),
    longitude: Number(plant?.location?.longitude),
    capacity: Number(plant?.capacity?.capacity.wattHour),
    isActive: toOptionalBoolean(plant?.isActive),
    identity: toIdentity(plant?.identity),
    statusLabel: translate(plant?.plantStatus as EPlantStatus),
    changelog: plant?.changelog ?? [],
    localTime: formatLocalTime("Etc/UTC"),
    lastUpdate: "",
    showLastUpdate: false,
    isStatusActive: isPlantStatusActive(plant?.plantStatus),
    logoSize: 18,
    errorLogZoneId: "",
    showTabs: true,
  };
};

const getScoutViewModel = (plant?: IScoutSolarPlantView): ISiteViewModel => {
  return {
    displayName: getSiteDisplayName(
      firstNonEmpty(plant?.siteName, plant?.plantName),
      plant?.siteAlias,
    ),
    originalName: plant?.plantName ?? "",
    siteAlias: plant?.siteAlias ?? "",
    address: plant?.address ?? "",
    latitude: Number(plant?.location?.latitude),
    longitude: Number(plant?.location?.longitude),
    capacity: Number(plant?.capacity?.capacity.wattHour),
    isActive: toOptionalBoolean(plant?.isActive),
    identity: toIdentity(plant?.identity),
    statusLabel: translate(plant?.plantStatus as EPlantStatus),
    changelog: plant?.changelog ?? [],
    localTime: formatLocalTime("Etc/UTC"),
    lastUpdate: formatLastUpdate(plant?.scoutLastSeenAt),
    showLastUpdate: true,
    isStatusActive: isPlantStatusActive(plant?.plantStatus),
    logoSize: 18,
    errorLogZoneId: "",
    showTabs: false,
  };
};

export const getSiteViewModel = (
  serviceProvider: ESolarServiceProvider,
  plants: SitePlants,
): ISiteViewModel => {
  const builders: Partial<Record<ESolarServiceProvider, () => ISiteViewModel>> =
    {
      [ESolarServiceProvider.VRM]: () => getVrmViewModel(plants.vrm),
      [ESolarServiceProvider.GOODWE]: () => getGoodWeViewModel(plants.goodWe),
      [ESolarServiceProvider.GROWATT]: () =>
        getGrowattViewModel(plants.growatt),
      [ESolarServiceProvider.POWERHUB]: () =>
        getPowerHubViewModel(plants.powerHub),
      [ESolarServiceProvider.SCOUT]: () => getScoutViewModel(plants.scout),
    };

  const builder = builders[serviceProvider];
  if (!builder) {
    return getGoodWeViewModel(plants.goodWe);
  }
  return builder();
};

export const getEnableDisableLabel = (
  isActive: boolean | undefined,
): string => {
  if (isActive) {
    return "Disable";
  }
  return "Enable";
};

export type PlantViewSetters = {
  setVrmSolarPlant: (plant?: IVrmSolarPlantView) => void;
  setGoodWeSolarPlant: (plant?: IGoodWeSolarPlantView) => void;
  setGroWattSolarPlantView: (plant?: IGroWattSolarPlantView) => void;
  setPowerHubSolarPlant: (plant?: IPowerHubSolarPlantView) => void;
  setScoutSolarPlant: (plant?: IScoutSolarPlantView) => void;
  setIsViewingSolarPlant: (value: boolean) => void;
};

export const applySolarPlantView = (
  solarPlantView: SolarPlantView,
  setters: PlantViewSetters,
) => {
  setters.setVrmSolarPlant(solarPlantView as IVrmSolarPlantView);
  setters.setGoodWeSolarPlant(solarPlantView as IGoodWeSolarPlantView);
  setters.setGroWattSolarPlantView(solarPlantView as IGroWattSolarPlantView);
  setters.setPowerHubSolarPlant(solarPlantView as IPowerHubSolarPlantView);
  setters.setScoutSolarPlant(solarPlantView as IScoutSolarPlantView);
  setters.setIsViewingSolarPlant(true);
};

export const clearSolarPlantView = (setters: PlantViewSetters) => {
  setters.setVrmSolarPlant(undefined);
  setters.setGoodWeSolarPlant(undefined);
  setters.setGroWattSolarPlantView(undefined);
  setters.setPowerHubSolarPlant(undefined);
  setters.setScoutSolarPlant(undefined);
};

const isGrowattServiceProvider = (
  serviceProvider: ESolarServiceProvider,
): boolean => {
  return serviceProvider === ESolarServiceProvider.GROWATT;
};

const isScoutServiceProvider = (
  serviceProvider: ESolarServiceProvider,
): boolean => {
  return serviceProvider === ESolarServiceProvider.SCOUT;
};

export const getScoutMeters = (
  plant?: IScoutSolarPlantView,
): IScoutSolarPlantMeter[] => {
  if (!plant?.scoutMeters) {
    return [];
  }
  return Object.values(plant.scoutMeters);
};

export const syncActiveTab = (
  activeTabState: number | undefined,
  setActiveTab: (tab: number) => void,
) => {
  if (activeTabState) {
    setActiveTab(activeTabState);
  }
};

export const loadPlantDetails = (
  plantId: string | undefined,
  trigger: PlantDetailsTrigger,
  onApply: (solarPlantView: SolarPlantView) => void,
  onClear: (error?: IHttpError) => void,
  onDone?: () => void,
) => {
  if (!plantId) {
    return;
  }

  trigger({ plantId })
    .unwrap()
    .then((res: IViewUserSolarPlantDetailsResponseDTO) => {
      onApply(res.solarPlantView);
    })
    .catch((error: IHttpError) => onClear(error))
    .finally(() => {
      if (onDone) {
        onDone();
      }
    });
};

export const loadRemotePlantDetails = (
  serviceProvider: ESolarServiceProvider,
  plantId: string | undefined,
  trigger: PlantDetailsTrigger,
  onApply: (solarPlantView: SolarPlantView) => void,
  onClear: (error?: IHttpError) => void,
  onDone?: () => void,
) => {
  if (
    isGrowattServiceProvider(serviceProvider) ||
    isScoutServiceProvider(serviceProvider)
  ) {
    return;
  }

  loadPlantDetails(plantId, trigger, onApply, onClear, onDone);
};

export const startRemotePlantPolling = (
  serviceProvider: ESolarServiceProvider,
  plantId: string | undefined,
  trigger: PlantDetailsTrigger,
  onApply: (solarPlantView: SolarPlantView) => void,
  onClear: () => void,
) => {
  if (
    isGrowattServiceProvider(serviceProvider) ||
    isScoutServiceProvider(serviceProvider)
  ) {
    return undefined;
  }

  const interval = window.setInterval(() => {
    loadPlantDetails(plantId, trigger, onApply, onClear);
  }, 5000);

  return () => window.clearInterval(interval);
};

const getStatusSuccessMessage = (isActive: boolean | undefined): string => {
  if (isActive) {
    return "Site disable and hide successfully";
  }
  return "Site enable successfully";
};

const getStatusErrorMessage = (isActive: boolean | undefined): string => {
  if (isActive) {
    return "Site disable and hide unsuccessfully";
  }
  return "Site enable unsuccessfully";
};

export const confirmSiteStatusChange = (
  identity: string | undefined,
  isActive: boolean | undefined,
  trigger: ChangeStatusTrigger,
  navigate: NavigateFunction,
) => {
  if (!identity) {
    return;
  }

  trigger({ plantId: identity })
    .unwrap()
    .then(() => {
      showSuccessMessage(getStatusSuccessMessage(isActive));
      navigate(AppRoute.ADMIN_SITES, {
        state: {
          updateCurrentState: true,
        },
      });
    })
    .catch(() => {
      showErrorMessage(getStatusErrorMessage(isActive));
    });
};
